import { ensureCSRFToken, fetchAPI } from "./backendClient";
import type { HouseholdInput, HouseholdReport, PersonInput } from "../types";

/**
 * Select the active value based on type.
 * Returns the percentage value if type is 'percentage', otherwise returns the amount value.
 * The backend will handle the conversion from percentage to absolute amount.
 */
function getActiveValue(
  percentageValue: number,
  amountValue: number,
  valueType: string | undefined
): number {
  if (valueType === 'percentage') {
    return percentageValue;
  }
  // Use amount value directly (type is 'amount' or undefined defaults to amount)
  return amountValue;
}

/**
 * Transform nested PersonInput to flattened backend format.
 * Sends raw values (amounts or percentages) along with metadata.
 * Backend handles conversion from percentage to absolute amounts.
 */
function flattenPersonInput(person: PersonInput, prefix: 'primary' | 'partner'): Record<string, unknown> {
  // For partner, keep old field names (partner_net_pension_contributions)
  // For primary, use new field names (primary_pension_contribution - singular)
  const pensionFieldName = prefix === 'primary' ? 'pension_contribution' : 'net_pension_contributions';
  const employerPensionFieldName = prefix === 'primary' ? 'employer_pension_contribution' : 'employer_pension_contributions';

  // For metadata fields, primary uses different naming than partner
  const pensionUnitField = prefix === 'primary'
    ? 'pension_contribution_unit'
    : 'net_pension_contributions_type';
  const employerPensionUnitField = prefix === 'primary'
    ? 'employer_pension_contribution_unit'
    : 'employer_pension_contributions_type';

  const result: Record<string, unknown> = {
    [`${prefix}_gross_salary`]: person.gross_salary ?? 0,
    [`${prefix}_salary_sacrifice_contributions`]: person.salary_sacrifice_contributions ?? 0,
    [`${prefix}_${pensionFieldName}`]: getActiveValue(
      person.net_pension_contributions_percentage ?? 0,
      person.net_pension_contributions_amount ?? 0,
      person.net_pension_contributions_type
    ),
    [`${prefix}_${pensionUnitField}`]: person.net_pension_contributions_type ?? 'amount',
    [`${prefix}_${employerPensionFieldName}`]: getActiveValue(
      person.employer_pension_contributions_percentage ?? 0,
      person.employer_pension_contributions_amount ?? 0,
      person.employer_pension_contributions_type
    ),
    [`${prefix}_${employerPensionUnitField}`]: person.employer_pension_contributions_type ?? 'amount',
    [`${prefix}_payroll_giving_donations`]: person.payroll_giving_donations ?? 0,
    [`${prefix}_gift_aid_donations`]: person.gift_aid_donations ?? 0,
    [`${prefix}_tax_code`]: person.tax_code ?? '1257L',
  };

  // Add primary-specific pension type
  if (prefix === 'primary' && person.pension_type) {
    result.primary_pension_contribution_type = person.pension_type;
  }

  // Add taxable benefits if present
  if (person.taxable_benefits !== undefined) {
    result[`${prefix}_taxable_benefits`] = person.taxable_benefits;
  }

  // Add bonus if present
  if (person.bonus !== undefined) {
    result[`${prefix}_bonus`] = person.bonus;
  }

  // Add investment income if present
  if (person.dividend_income !== undefined) {
    result[`${prefix}_dividend_income`] = person.dividend_income;
  }
  if (person.interest_income !== undefined) {
    result[`${prefix}_interest_income`] = person.interest_income;
  }

  return result;
}

/**
 * Submits household data to the calculator API.
 *
 * @param payload - Household input data
 * @returns Promise resolving to HouseholdReport
 */
export async function submitHousehold(
  payload: HouseholdInput
): Promise<HouseholdReport> {
  // Ensure CSRF token exists for anonymous users
  await ensureCSRFToken();

  // Transform nested format to flattened format expected by backend
  const flattenedPayload: Record<string, unknown> = {
    ...flattenPersonInput(payload.primary, 'primary'),
    ...(payload.partner ? flattenPersonInput(payload.partner, 'partner') : {}),
    children: payload.children ?? [],
    claims_child_benefit: payload.claims_child_benefit ?? false,
  };

  // Include tax_year if specified
  if (payload.tax_year) {
    flattenedPayload.tax_year = payload.tax_year;
  }

  const resp = await fetchAPI("/v1/calculator/", {
    method: "POST",
    body: JSON.stringify(flattenedPayload),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({}));
    const message = errorData?.error || errorData?.detail || `HTTP ${resp.status}`;
    throw new Error(message);
  }

  return resp.json();
}